/**
 * gap 1 / DEC-383: where a hauling line's rate came from. Stored as billable_events.rate_source (v: 1).
 * Shown in In Review and billing ONLY; never on the invoice, PDF or accounting export (D-15).
 * OPEN-213 widens: adds ZONE / DISPOSAL_FEE / OVERRIDE kinds and widens RateFallbackReason.
 *
 * Exported as the `@lodex-pro/shared/rate-source` subpath, never from the root barrel: web bundles
 * import it, so it pulls in nothing but ./money (no drizzle, no zod).
 */
import { formatCentsAsDollars } from './money.js';

export type RatedBillingMethod = 'PER_BBL' | 'HOURLY' | 'PER_LOAD';

export const RATED_BILLING_METHODS: readonly RatedBillingMethod[] = [
  'PER_BBL',
  'HOURLY',
  'PER_LOAD',
];

export function isRatedBillingMethod(m: string): m is RatedBillingMethod {
  return (RATED_BILLING_METHODS as readonly string[]).includes(m);
}

/** Why a fallback step priced the line. gap 1 has none; OPEN-213 widens it to its zone flag codes. */
export type RateFallbackReason = never;

export type RateSource =
  | {
      readonly v: 1;
      readonly kind: 'CUSTOMER_JOB_TYPE';
      readonly effectiveFrom: string;
      readonly revision: number;
    }
  // `RateFallbackReason | null` is just `null` while the reason is `never`; it is spelled out so OPEN-213's
  // widening of RateFallbackReason flows into these two kinds with no edit here.
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | { readonly v: 1; readonly kind: 'CUSTOMER'; readonly reason: RateFallbackReason | null }
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  | { readonly v: 1; readonly kind: 'JOB_TYPE'; readonly reason: RateFallbackReason | null };

export type RateSourceKind = RateSource['kind'];

export const RATE_SOURCE_LABELS: Readonly<Record<RateSourceKind, string>> = {
  CUSTOMER_JOB_TYPE: 'customer job-type rate',
  CUSTOMER: 'customer rate',
  JOB_TYPE: 'standard rate',
};

export function rateUnitSuffix(billingMethod: string): '/bbl' | '/h' | '/load' | '' {
  switch (billingMethod) {
    case 'PER_BBL':
      return '/bbl';
    case 'HOURLY':
      return '/h';
    case 'PER_LOAD':
      return '/load';
    default:
      return '';
  }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Calendar-day 'YYYY-MM-DD' -> parts, or null. String parts only, never Date/UTC (DEC-004). */
function isoDayParts(iso: string): { year: string; month: number; day: number } | null {
  const m = ISO_DAY.exec(iso);
  if (!m) return null;
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  return { year: m[1]!, month, day };
}

/** 'Jan 1' when the year matches todayIso's, else 'Jan 1, 2027'. */
function formatFromDay(iso: string, todayIso?: string): string {
  const parts = isoDayParts(iso);
  if (!parts) return iso;
  const short = `${MONTHS[parts.month - 1]} ${parts.day}`;
  const today = todayIso === undefined ? null : isoDayParts(todayIso);
  return today !== null && today.year === parts.year ? short : `${short}, ${parts.year}`;
}

/**
 * The In Review / billing tag: money and unit, then " · " and the source label; a customer x job-type price
 * adds "(from <start day>)". todayIso (company-local 'YYYY-MM-DD') drops the year when it matches; omitted
 * -> the year is always shown. Dates are calendar days: formatted from the string parts, never through
 * Date/UTC (DEC-004). A null rate is 'No rate' whatever the source; a null source shows the rate alone
 * (lines approved before the tag existed). Callers pass the JOB's billing method.
 */
export function rateTag(
  rateCents: number | null,
  billingMethod: string,
  source: RateSource | null,
  todayIso?: string,
): string {
  if (rateCents === null) return 'No rate';
  const money = `$${formatCentsAsDollars(rateCents)}${rateUnitSuffix(billingMethod)}`;
  if (source === null) return money;
  const label = RATE_SOURCE_LABELS[source.kind];
  if (source.kind === 'CUSTOMER_JOB_TYPE') {
    return `${money} · ${label} (from ${formatFromDay(source.effectiveFrom, todayIso)})`;
  }
  return `${money} · ${label}`;
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw);
}

/** Tolerant reader of the jsonb column: anything not a known v:1 shape -> null. Never throws. */
export function parseRateSource(raw: unknown): RateSource | null {
  if (!isRecord(raw) || raw.v !== 1) return null;
  switch (raw.kind) {
    case 'CUSTOMER_JOB_TYPE': {
      const { effectiveFrom, revision } = raw;
      if (typeof effectiveFrom !== 'string' || isoDayParts(effectiveFrom) === null) return null;
      if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0) return null;
      return { v: 1, kind: 'CUSTOMER_JOB_TYPE', effectiveFrom, revision };
    }
    case 'CUSTOMER':
    case 'JOB_TYPE':
      // gap 1 has no fallback reasons (RateFallbackReason = never): only null / absent is known.
      if (raw.reason !== null && raw.reason !== undefined) return null;
      return { v: 1, kind: raw.kind, reason: null };
    default:
      return null;
  }
}
